import { format, isValid, parse } from "date-fns";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  InputAdornment,
  Loading,
  MenuItem,
  Paper,
  Select,
  Tab,
  TabPanel,
  Tabs,
  TextField,
  TimePicker,
  Tooltip,
} from "../../../../components";
import {
  ConfiguracoesAgendaFuncionamentoService,
  ConfiguracoesService,
  notification,
} from "../../../../services";
import { responseErros } from "../../../../utils";
import { ConfiguracoesEntity } from "../../entities";

const formatHora = (time) => {
  if (!time) return null;
  const formato = time.length === 5 ? "HH:mm" : "HH:mm:ss";
  const parsed = parse(time, formato, new Date());
  return isValid(parsed) ? parsed : null;
};

function IntervaloHorariosComponent({
  intervalos,
  horario,
  index,
  setHorarios,
  abaValue,
  validarIntervalo,
}) {
  const handleChangeHorario = (name, date, intervalos, index) => {
    let dataFormatada = null;
    if (isValid(date)) {
      dataFormatada = format(date, "HH:mm");
    }

    setHorarios((prev) => {
      const horarios = [...prev];
      const intervalos = horarios.find(
        (item) => item.diaSemana === abaValue,
      ).intervalos;
      intervalos[index][name] = dataFormatada;
      return horarios;
    });
  };

  const handleAdicionarIntervalo = () => {
    setHorarios((prev) => {
      const horarios = [...prev];
      const intervalos = horarios.find(
        (item) => item.diaSemana === abaValue,
      ).intervalos;
      intervalos.push({ abertura: null, fechamento: null });
      return horarios;
    });
  };

  const handleRemoverIntervalo = (index) => {
    setHorarios((prev) => {
      const horarios = [...prev];
      const intervalos = horarios.find(
        (item) => item.diaSemana === abaValue,
      ).intervalos;
      intervalos.splice(index, 1);
      return horarios;
    });
  };

  return (
    <>
      <Grid item xs={5.5}>
        <TimePicker
          id="horaAbertura"
          key={index}
          name="horaAbertura"
          label="Abertura"
          format="HH:mm"
          minTime={
            index > 0
              ? formatHora(intervalos[index - 1].fechamento)
              : undefined
          }
          maxTime={formatHora(horario.fechamento)}
          value={formatHora(horario.abertura)}
          onChange={(date) =>
            handleChangeHorario("abertura", date, intervalos, index)
          }
        />
      </Grid>
      <Grid item xs={5.5}>
        <TimePicker
          id="horaFechamento"
          key={index}
          name="horaFechamento"
          label="Fechamento"
          format="HH:mm"
          minTime={formatHora(horario.abertura)}
          value={formatHora(horario.fechamento)}
          onChange={(date) =>
            handleChangeHorario("fechamento", date, intervalos, index)
          }
        />
      </Grid>
      <Grid
        item
        xs={1}
        sx={{ display: "flex", justifyContent: "center", gap: 2 }}
      >
        {index > 0 && (
          <Tooltip title="Remover intervalo" arrow placement="bottom">
            <Button
              id="buttonRemoverIntervalo"
              key={index}
              variant="contained"
              size="small"
              color="error"
              onClick={() => handleRemoverIntervalo(index)}
              sx={{
                height: "35px",
                borderRadius: "20%",
                minWidth: "15px",
                boxShadow: "none",
              }}
            >
              <i
                className="ph-bold ph-minus"
                style={{ color: "#fff", fontSize: 15 }}
              />
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Adicionar novo intervalo" arrow placement="bottom">
          <Button
            id="buttonAdicionarIntervalo"
            key={index}
            variant="contained"
            size="small"
            disabled={validarIntervalo(intervalos, index)}
            onClick={handleAdicionarIntervalo}
            sx={{
              height: "35px",
              borderRadius: "20%",
              minWidth: "15px",
              boxShadow: "none",
            }}
          >
            <i
              className="ph-bold ph-plus"
              style={{ color: "#fff", fontSize: 15 }}
            />
          </Button>
        </Tooltip>
      </Grid>
    </>
  );
}

function tabProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

function AgendaFuncionamentoDialog({ open, setOpen, configuracao, onChange }) {
  const diasSemana = [
    { sigla: "D", label: "Domingo" },
    { sigla: "S", label: "Segunda" },
    { sigla: "T", label: "Terça" },
    { sigla: "Q", label: "Quarta" },
    { sigla: "Q", label: "Quinta" },
    { sigla: "S", label: "Sexta" },
    { sigla: "S", label: "Sábado" },
  ];
  const fatorHora = configuracao?.intervaloAgendamentos % 60;

  const [diasSelecionados, setDiasSelecionados] = useState(
    configuracao?.diasSemanaFuncionamento || [0, 1, 2, 3, 4, 5, 6],
  );
  const [horarios, setHorarios] = useState(
    diasSelecionados.map((item) => ({
      diaSemana: item,
      intervalos: [
        {
          abertura: null,
          fechamento: null,
        },
      ],
    })),
  );
  const [intervaloDuracao, setIntervaloDuracao] = useState({
    tempo:
      fatorHora === 0
        ? configuracao?.intervaloAgendamentos / 60
        : configuracao?.intervaloAgendamentos,
    unidade: fatorHora === 0 ? "HORA" : "MIN",
  });
  const [abaValue, setAbaValue] = useState(
    configuracao?.diasSemanaFuncionamento?.[0] || 0,
  );
  const [loading, setLoading] = useState(false);

  const agendaFuncionamentoService =
    new ConfiguracoesAgendaFuncionamentoService();
  const configuracoesService = new ConfiguracoesService();

  useEffect(() => {
    buscarConfiguracaoAgendaFuncionamento();
  }, []);

  const buscarConfiguracaoAgendaFuncionamento = async () => {
    const result = await agendaFuncionamentoService.find();
    if (!result.isAxiosError) {
      if (result.data.length) {
        setHorarios(result.data);
      }
    } else {
      responseErros(result);
    }
  };

  const handleSelecaoDia = (dia) => {
    let dias = [];
    if (diasSelecionados.includes(dia)) {
      dias = diasSelecionados.filter((d) => d !== dia);
      if (!dias.length) {
        notification.erroGenericos(
          "Pelo menos 1 dia da semana deve estar habilitado",
        );
        return;
      }
      setHorarios((prev) => prev.filter((item) => item.diaSemana !== dia));
    } else {
      dias = [...diasSelecionados, dia];
      setHorarios((prev) => [
        ...prev,
        { diaSemana: dia, intervalos: [{ abertura: null, fechamento: null }] },
      ]);
    }

    dias.sort((a, b) => a - b);
    setAbaValue(dias[0]);
    setDiasSelecionados(dias);
  };

  const validarIntervalo = (intervalos, index) => {
    const intervaloAtual = intervalos[index],
      intervaloAnterior = index > 0 ? intervalos[index - 1] : null;
    const abertura = intervaloAtual.abertura,
      fechamento = intervaloAtual.fechamento;

    return (
      !abertura ||
      !fechamento ||
      formatHora(abertura) > formatHora(fechamento) ||
      (intervaloAnterior &&
        formatHora(intervaloAnterior.fechamento) >
          formatHora(abertura))
    );
  };

  const renderIntervalosHorario = (item) => {
    const intervalos = horarios.find(
      (dia) => dia.diaSemana === item,
    ).intervalos;
    return intervalos.map((horario, index) => (
      <IntervaloHorariosComponent
        key={index}
        intervalos={intervalos}
        horario={horario}
        index={index}
        setHorarios={setHorarios}
        abaValue={abaValue}
        validarIntervalo={validarIntervalo}
      />
    ));
  };

  const handleCloseDialog = () => setOpen(false);

  async function updateConfiguracoes() {
    const configuracaoTemp = {
      ...configuracao,
      diasSemanaFuncionamento: diasSelecionados,
      intervaloAgendamentos:
        intervaloDuracao.unidade === "HORA"
          ? intervaloDuracao.tempo * 60
          : intervaloDuracao.tempo,
    };
    const configuracoesEntity = new ConfiguracoesEntity(configuracaoTemp);
    const updateResponse = await configuracoesService.atualizar(
      configuracao.id,
      configuracoesEntity,
    );
    if (!updateResponse.isAxiosError) {
      onChange({
        target: {
          name: "intervaloAgendamentos",
          value:
            intervaloDuracao.unidade === "HORA"
              ? intervaloDuracao.tempo * 60
              : Number(intervaloDuracao.tempo),
        },
      });

      onChange({
        target: {
          name: "diasSemanaFuncionamento",
          value: diasSelecionados,
        },
      });
    } else {
      responseErros(updateResponse);
    }
  }

  async function handleSubmit() {
    setLoading(true);

    let horariosValidos = true;
    for (const horario of horarios) {
      const erroValidacao = horario.intervalos.find((intervalo, index, array) =>
        validarIntervalo(array, index),
      );

      if (erroValidacao) {
        setAbaValue(horario.diaSemana);
        notification.erroGenericos(
          "Todos os intervalos devem ser preenchidos corretamente",
        );
        horariosValidos = false;
        break;
      }
    }

    if (horariosValidos) {
      const result = await agendaFuncionamentoService.upsert(horarios);
      if (!result.isAxiosError) {
        notification.alteracaoSucesso();
        await updateConfiguracoes();
        handleCloseDialog();
      } else {
        responseErros(result);
      }
    }

    setLoading(false);
  }

  return (
    <Dialog maxWidth={"lg"} fullWidth open={open} onClose={handleCloseDialog}>
      <DialogContent>
        <CardHeader title="Agenda de Funcionamento" />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            ml: 1,
            mt: 2,
          }}
        >
          <span className="mt-2">Dias da semana</span>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              mr: 1,
            }}
          >
            {diasSemana.map((item, index) => (
              <Chip
                key={index}
                label={item.sigla}
                clickable
                onClick={() => handleSelecaoDia(index)}
                color={diasSelecionados.includes(index) ? "primary" : "default"}
                variant={
                  diasSelecionados.includes(index) ? "filled" : "outlined"
                }
                sx={{
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8125rem",
                  },
                  height: {
                    xs: 28,
                    sm: 32,
                    md: 36,
                  },
                  width: {
                    xs: 28,
                    sm: 32,
                    md: 36,
                  },
                  borderRadius: "50%",
                  "& .MuiChip-label": {
                    px: 0,
                    whiteSpace: "nowrap",
                  },
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: 1,
                  },
                }}
              />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            ml: 1,
            mt: 3,
          }}
        >
          <span className="mt-2">Duração do intervalo entre agendamentos</span>
          <div style={{ maxWidth: "200px", marginRight: "8px" }}>
            <TextField
              id="intervaloDuracao"
              name="intervaloDuracao"
              label="Duração"
              variant="outlined"
              margin="normal"
              value={intervaloDuracao.tempo || ""}
              onChange={(e) =>
                setIntervaloDuracao((prev) => ({
                  ...prev,
                  tempo: e.target.value,
                }))
              }
              fullWidth
              type="number"
              disableNumberArrows
              sx={{
                "& .MuiInputBase-root": {
                  paddingRight: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      id="unidadeDuracao"
                      name="unidadeDuracao"
                      disableClearable
                      value={intervaloDuracao.unidade || "MIN"}
                      onChange={(e) =>
                        setIntervaloDuracao((prev) => ({
                          ...prev,
                          unidade: e.target.value,
                        }))
                      }
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": { paddingRight: "24px" },
                      }}
                    >
                      <MenuItem value="MIN" sx={{ fontSize: 12 }}>
                        minutos
                      </MenuItem>
                      <MenuItem value="HORA" sx={{ fontSize: 12 }}>
                        horas
                      </MenuItem>
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </Box>
        <Paper sx={{ mt: 3 }}>
          <Tabs
            value={abaValue}
            onChange={(e, value) => {
              e.persist();
              setAbaValue(value);
            }}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            {diasSelecionados.map((item) => (
              <Tab
                label={diasSemana[item].label}
                {...tabProps(item)}
                value={item}
                key={item}
              />
            ))}
          </Tabs>
        </Paper>
        <CardContent sx={{ p: 0 }}>
          {diasSelecionados.map((item) => (
            <TabPanel value={abaValue} index={item} key={item}>
              <Grid container spacing={2} className="align-items-center mt-2">
                {renderIntervalosHorario(item)}
              </Grid>
            </TabPanel>
          ))}
        </CardContent>
        <DialogActions className="px-4 pb-4 mt-4">
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Salvar
          </Button>
          <Button
            variant="contained"
            color="padrao"
            onClick={handleCloseDialog}
          >
            Fechar
          </Button>
        </DialogActions>
      </DialogContent>
      <Loading loading={loading} />
    </Dialog>
  );
}

export default AgendaFuncionamentoDialog;
